import { Empty } from "antd";
import classNames from "classnames";
import BrandingChangesModal from "components/GlobalModals/BrandingChangesModal";
import { AnimatePresence } from "framer-motion";
import { useScrollToElementOnHashChange } from "hooks/useScrollToElementOnHashChange";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { pollGeneratedPostsThunk } from "store/posts/postActions";
import {
  GENERATED_POST_CONTAINER_ID,
  Post,
  PostScreen,
} from "store/posts/postConstants";
import { selectPostStore } from "store/posts/postSlice";
import GeneratedPostsEmptyScreen from "./components/GeneratedPostsEmptyScreen";
import PostsPageHeader from "./components/Header/PostsPageHeader";
import PostLoaderSkeleton from "./components/PostLoaderSkeleton";
import LoadingIndicator from "./components/Utility/LoadingIndicator";
import PostTour from "./components/Utility/PostTour";
import PostCardGrid from "./components/Views/PostCardGrid";
import PostCardList from "./components/Views/PostCardList";
import PostTableDisplay from "./components/Views/PostCardTable";
import GenerateMorePostsButton from "./Generate/GenerateMorePostsButton";
import ImproveYourPosts from "./ImproveYourPosts/ImproveYourPosts";
import PostSelectionProvider from "./PostSelection/PostSelectionProvider";
import ScheduledPostsList from "./ScheduledPosts/ScheduledPostsList";

export const POSTS_PAGE_ROUTE = "/social-posts";

type LayoutView = "grid" | "list" | "table";

const PostsPage = ({ postScreen }: { postScreen: PostScreen }) => {
  const [, setSearchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const { isAgencyMember } = useAppSelector((state) => state.user);
  const postData = useAppSelector(selectPostStore);
  const {
    isInitialized,
    postList: allPosts,
    nextPageUrl,
  } = postData[postScreen];

  const isGeneratedTab = postScreen === "generated";
  const isScheduledTab = postScreen === "scheduled";
  const enableReviewStatusFilter = isAgencyMember && postScreen === "draft";

  const [isApplyingBrandingChanges, setIsApplyingBrandingChanges] =
    useState(false);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [layout, setLayout] = useState<LayoutView>("list");
  const [isSelectionMode, setIsSelectionMode] = useState(false);

  const postList = enableReviewStatusFilter ? filteredPosts : allPosts;

  const toggleSelectionMode = useCallback(
    () => setIsSelectionMode((prev) => !prev),
    []
  );

  useEffect(() => {
    if (
      isGeneratedTab &&
      postData.generated.loadingCount &&
      !postData.generated.isGenerating
    ) {
      dispatch(pollGeneratedPostsThunk());
    }
  }, [
    isGeneratedTab,
    postData.generated.isGenerating,
    postData.generated.loadingCount,
    dispatch,
  ]);

  useEffect(() => {
    if (enableReviewStatusFilter) {
      setFilteredPosts(allPosts);
    }
  }, [allPosts, enableReviewStatusFilter]);

  useEffect(() => {
    setIsSelectionMode(false);
  }, [postScreen]);

  useScrollToElementOnHashChange(
    GENERATED_POST_CONTAINER_ID,
    "#bottom",
    { behavior: "smooth", block: "end" },
    50
  );

  const renderPostContent = () => {
    if (isScheduledTab) {
      return <ScheduledPostsList />;
    }

    const hasPostsToRender = postList.length > 0;
    const underlyingStoreHasPosts = allPosts.length > 0;

    if (isGeneratedTab) {
      const {
        isGenerating,
        loadingCount,
        postList: generatedPostListStore,
      } = postData.generated;
      if (
        generatedPostListStore.length === 0 &&
        (isGenerating || (loadingCount && loadingCount > 0))
      ) {
        return (
          <LoadingIndicator className="mx-auto my-auto">
            Generating posts...
          </LoadingIndicator>
        );
      }
      if (isApplyingBrandingChanges || generatedPostListStore.length === 0) {
        return (
          <GeneratedPostsEmptyScreen
            isApplyingBrandingChanges={isApplyingBrandingChanges}
          />
        );
      }
    }

    if (
      enableReviewStatusFilter &&
      !hasPostsToRender &&
      underlyingStoreHasPosts
    ) {
      return (
        <Empty
          className="my-auto"
          description="Nothing to show, try clearing your filters"
        />
      );
    }

    if (!hasPostsToRender) {
      if (!underlyingStoreHasPosts && !isGeneratedTab) {
        return <Empty className="my-auto" description="No posts available" />;
      } else if (underlyingStoreHasPosts) {
        return (
          <Empty
            className="my-auto"
            description="No posts match your current filters"
          />
        );
      }
    }

    if (layout === "table") {
      return <PostTableDisplay postList={postList} />;
    }

    if (layout === "grid") {
      return <PostCardGrid key={`${postScreen}-grid`} postList={postList} />;
    }

    return <PostCardList key={`${postScreen}-list`} postList={postList} />;
  };

  return (
    <AnimatePresence>
      <PostsPageHeader
        {...{ postList, setSearchParams, postScreen }}
        layout={layout}
        setLayout={setLayout}
        allPosts={allPosts}
        setFilteredPosts={setFilteredPosts}
        enableReviewStatusFilter={enableReviewStatusFilter}
        showLayoutSwitcher={!isScheduledTab}
        isSelectionMode={isSelectionMode}
        toggleSelectionMode={toggleSelectionMode}
      />

      {isInitialized ? (
        <div key="posts-content" className="flex flex-col flex-grow">
          <PostSelectionProvider
            {...{ postScreen, postList }}
            controlledSelectionMode={isSelectionMode}
            onExitSelectionMode={toggleSelectionMode}
          >
            <div
              id={isGeneratedTab ? GENERATED_POST_CONTAINER_ID : undefined}
              className={classNames(
                "flex flex-col gap-7 items-center max-w-screen-2xl mx-auto min-h-fit pt-12",
                {
                  "!gap-12": isScheduledTab,
                  "h-full !gap-0 justify-center": !postList.length,
                  "pb-48": postList.length,
                }
              )}
            >
              {renderPostContent()}

              {nextPageUrl && (
                <LoadingIndicator className="mx-auto">
                  Loading more posts
                </LoadingIndicator>
              )}

              {isGeneratedTab &&
                (!postData.generated.isGenerating || postList.length > 0) && (
                  <>
                    <GenerateMorePostsButton />
                    <ImproveYourPosts />
                    <BrandingChangesModal
                      setIsApplyingBrandingChanges={
                        setIsApplyingBrandingChanges
                      }
                    />
                    <PostTour />
                  </>
                )}
            </div>
          </PostSelectionProvider>
        </div>
      ) : (
        <div key="posts-loader">
          <PostLoaderSkeleton postScreen={postScreen} />
        </div>
      )}
    </AnimatePresence>
  );
};

export default PostsPage;
